/**
 * EscalationSettingsSection - Manages ticket escalation & reminder intervals for an organization
 *
 * For OrgAdmin users only. Allows configuring how quickly reminders and escalations
 * are sent for stale tickets, with separate intervals for normal/urgent and assigned/unassigned tickets.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { View, StyleSheet, Pressable, ActivityIndicator, TextInput } from 'react-native';
import ToggleSwitch from '../ToggleSwitch';
import { Feather } from '@expo/vector-icons';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';

import CustomText from '../CustomText';
import AppStyles from '../../AppStyles';
import Util from '../../util/util';
import { useTranslation } from '../../i18n';

// Minimal fallback used only during initial load before the API responds with authoritative defaults.
const INITIAL_SETTINGS = {
  enabled: false,
};

/** Shallow-equal check for flat settings objects (order-independent) */
const settingsEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((key) => a[key] === b[key]);
};

/**
 * Inline date editor for the cutoff date (enabledAt timestamp).
 * Shows the date as MM/DD/YYYY and lets the user type a new date.
 */
const CutoffDateInput = ({ value, onChange, readOnly = false }) => {
  const { t } = useTranslation('admin');

  const formatted = useMemo(() => {
    if (!value) return '';
    const d = new Date(value);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  }, [value]);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(formatted);

  const handleSubmit = () => {
    setEditing(false);
    // Parse MM/DD/YYYY explicitly to avoid cross-platform Date constructor inconsistencies
    const parts = draft.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (parts) {
      const parsed = new Date(Number(parts[3]), Number(parts[1]) - 1, Number(parts[2]));
      if (!isNaN(parsed.getTime())) {
        parsed.setHours(0, 0, 0, 0);
        onChange(parsed.getTime());
        return;
      }
    }
    setDraft(formatted);
  };

  return (
    <View style={styles.cutoffDateRow}>
      <Feather name="calendar" size={14} color={AppStyles.colorSet.textSecondary} />
      {readOnly ? (
        <CustomText font="semiBold" size={15} color={AppStyles.colorSet.textPrimary} style={{ marginLeft: 8 }}>
          {formatted}
        </CustomText>
      ) : editing ? (
        <TextInput
          style={styles.cutoffDateInput}
          value={draft}
          onChangeText={setDraft}
          onBlur={handleSubmit}
          onSubmitEditing={handleSubmit}
          placeholder={t('escalation.datePlaceholder')}
          autoFocus
          selectTextOnFocus
        />
      ) : (
        <Pressable
          onPress={() => { setDraft(formatted); setEditing(true); }}
          style={{ cursor: 'pointer' }}
        >
          {({ hovered, focused }) => (
            <CustomText
              font="semiBold"
              size={15}
              color={hovered || focused ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textPrimary}
              style={{ marginLeft: 8, textDecorationLine: (hovered || focused) ? 'underline' : 'none' }}
            >
              {formatted}
            </CustomText>
          )}
        </Pressable>
      )}
      {!readOnly && (
        <CustomText size={12} color={AppStyles.colorSet.textMuted} style={{ marginLeft: 8 }}>
          {t('escalation.tapToEdit')}
        </CustomText>
      )}
    </View>
  );
};

/**
 * @param {string} organization - Organization name
 */
const EscalationSettingsSection = ({ organization, readOnly = false }) => {
  const { t } = useTranslation('admin');

  const [settings, setSettings] = useState({ ...INITIAL_SETTINGS });
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [savedSettings, setSavedSettings] = useState(null);
  const [userEditedCutoff, setUserEditedCutoff] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!organization) return;

    setLoading(true);
    setError(null);

    try {
      const response = await Util.get('Resty', '/items/organization/escalation-settings', {
        queryParams: { organization },
      });

      const fetchedDefaults = response?.defaults || {};
      const fetched = response?.data || { ...fetchedDefaults };
      setSettings(fetched);
      setSavedSettings(fetched);
      setDefaults(fetchedDefaults);
      setHasChanges(false);
    } catch (err) {
      console.error('Error fetching escalation settings:', err);
      setError(t('escalation.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [organization]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleChange = (key, value) => {
    if (readOnly) return;
    const newSettings = { ...settings, [key]: value };

    // When toggling escalation on, preview the new cutoff date immediately
    // — unless the user has manually edited the cutoff date this session
    if (key === 'enabled' && value === true && !settings.enabled && !userEditedCutoff) {
      newSettings.enabledAt = Date.now();
    }

    // Track when the user explicitly changes the cutoff date
    if (key === 'enabledAt') {
      setUserEditedCutoff(true);
    }

    setSettings(newSettings);
    setHasChanges(!settingsEqual(newSettings, savedSettings));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const response = await Util.patch('Resty', '/items/organization/escalation-settings', {
        queryParams: { organization },
        body: { organization, settings },
      });

      if (response?.error) {
        setError(response.error);
        return;
      }

      const saved = response?.data || settings;
      setSettings(saved);
      setSavedSettings(saved);
      setHasChanges(false);
      setUserEditedCutoff(false);
    } catch (err) {
      console.error('Error saving escalation settings:', err);
      const errorMessage =
        err?.response?.data?.error || err?.message || t('escalation.saveFailed');
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleResetDefaults = () => {
    if (!defaults) return;
    // Only reset interval thresholds — preserve toggles and cutoff date
    const resetSettings = {
      ...settings,
      reminderIntervalHours: defaults.reminderIntervalHours,
      escalationIntervalHours: defaults.escalationIntervalHours,
      urgentReminderIntervalHours: defaults.urgentReminderIntervalHours,
      urgentEscalationIntervalHours: defaults.urgentEscalationIntervalHours,
      unassignedReminderIntervalHours: defaults.unassignedReminderIntervalHours,
      unassignedEscalationIntervalHours: defaults.unassignedEscalationIntervalHours,
      unassignedUrgentReminderIntervalHours: defaults.unassignedUrgentReminderIntervalHours,
      unassignedUrgentEscalationIntervalHours: defaults.unassignedUrgentEscalationIntervalHours,
      ticketCutoffDays: defaults.ticketCutoffDays,
    };
    setSettings(resetSettings);
    setHasChanges(!settingsEqual(resetSettings, savedSettings));
  };

  const [editingKey, setEditingKey] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const getStep = (value, unit = 'hours') => {
    if (unit === 'days') {
      if (value >= 60) return 30;
      if (value >= 14) return 7;
      return 1;
    }
    if (value >= 48) return 12;
    if (value >= 24) return 6;
    return 1;
  };

  const handleStepDown = (key, value, unit = 'hours') => {
    const step = getStep(value, unit);
    handleChange(key, Math.max(1, value - step));
  };

  const handleStepUp = (key, value, unit = 'hours') => {
    const step = getStep(value, unit);
    handleChange(key, value + step);
  };

  const handleValueTap = (key, value) => {
    setEditingKey(key);
    setEditingValue(String(value));
  };

  const handleValueSubmit = (key) => {
    const parsed = parseInt(editingValue, 10);
    if (!isNaN(parsed) && parsed >= 1) {
      handleChange(key, parsed);
    }
    setEditingKey(null);
  };

  // Warn when escalation interval is shorter than reminder interval
  const intervalWarnings = useMemo(() => {
    const warnings = {};
    const pairs = [
      ['reminderIntervalHours', 'escalationIntervalHours', t('escalation.pair.assignedNormal')],
      ['urgentReminderIntervalHours', 'urgentEscalationIntervalHours', t('escalation.pair.assignedUrgent')],
      ['unassignedReminderIntervalHours', 'unassignedEscalationIntervalHours', t('escalation.pair.unassignedNormal')],
      ['unassignedUrgentReminderIntervalHours', 'unassignedUrgentEscalationIntervalHours', t('escalation.pair.unassignedUrgent')],
    ];
    for (const [rKey, eKey] of pairs) {
      if (settings[eKey] != null && settings[rKey] != null && settings[eKey] < settings[rKey]) {
        warnings[eKey] = t('escalation.beforeReminderWarning', {
          escalation: settings[eKey],
          reminder: settings[rKey],
        });
      }
    }
    return warnings;
  }, [settings]);

  const renderIntervalInput = (label, key, description, unit = 'hours') => {
    const value = settings[key];
    const isEditing = editingKey === key;
    const step = getStep(value, unit === 'hours' ? 'hours' : 'days');
    const unitLabel = unit === 'days' ? 'days' : 'hrs';
    const warning = intervalWarnings[key];
    return (
      <View style={styles.inputRow} key={key}>
        <View style={styles.inputLabelContainer}>
          <CustomText font="medium" size={14} color={AppStyles.colorSet.textPrimary}>
            {label}
          </CustomText>
          {description && (
            <CustomText size={12} color={AppStyles.colorSet.textMuted}>
              {description}
            </CustomText>
          )}
          {warning && (
            <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 2 }}>
              <Feather name="alert-triangle" size={11} color="#D97706" />
              <CustomText size={11} color="#D97706" style={{ marginLeft: 4, flex: 1 }}>
                {warning}
              </CustomText>
            </View>
          )}
        </View>
        {readOnly ? (
          <View style={[styles.inputContainer, styles.valueDisplay]}>
            <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
              {value}
            </CustomText>
            <CustomText size={11} color={AppStyles.colorSet.textMuted}>
              {unitLabel}
            </CustomText>
          </View>
        ) : (
          <View style={styles.inputContainer}>
            <Pressable
              style={({ hovered, focused }) => [
                styles.stepButton,
                { cursor: 'pointer' },
                (hovered || focused) && styles.stepButtonHovered,
              ]}
              onPress={() => handleStepDown(key, value, unit === 'hours' ? 'hours' : 'days')}
            >
              <CustomText font="medium" size={14} color={AppStyles.colorSet.textSecondary}>
                {step > 1 ? `−${step}` : '−'}
              </CustomText>
            </Pressable>
            <Pressable
              style={({ hovered, focused }) => [
                styles.valueDisplay,
                { cursor: 'pointer' },
                (hovered || focused) && styles.valueDisplayHovered,
              ]}
              onPress={() => handleValueTap(key, value)}
            >
              {isEditing ? (
                <TextInput
                  style={styles.valueInput}
                  value={editingValue}
                  onChangeText={setEditingValue}
                  onBlur={() => handleValueSubmit(key)}
                  onSubmitEditing={() => handleValueSubmit(key)}
                  keyboardType="number-pad"
                  autoFocus
                  selectTextOnFocus
                />
              ) : (
                <>
                  <CustomText font="semiBold" size={16} color={AppStyles.colorSet.textPrimary}>
                    {value}
                  </CustomText>
                  <CustomText size={11} color={AppStyles.colorSet.textMuted}>
                    {unitLabel}
                  </CustomText>
                </>
              )}
            </Pressable>
            <Pressable
              style={({ hovered, focused }) => [
                styles.stepButton,
                { cursor: 'pointer' },
                (hovered || focused) && styles.stepButtonHovered,
              ]}
              onPress={() => handleStepUp(key, value, unit === 'hours' ? 'hours' : 'days')}
            >
              <CustomText font="medium" size={14} color={AppStyles.colorSet.textSecondary}>
                {step > 1 ? `+${step}` : '+'}
              </CustomText>
            </Pressable>
          </View>
        )}
      </View>
    );
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={AppStyles.colorSet.brandPrimary} />
          <CustomText style={styles.loadingText} size={14} color={AppStyles.colorSet.textSecondary}>
            {t('escalation.loading')}
          </CustomText>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <CustomText
        style={styles.heading}
        font="semiBold"
        size={18}
        color={AppStyles.colorSet.textPrimary}
      >
        {t('escalation.heading')}
      </CustomText>
      <CustomText
        style={styles.explanationText}
        font="regular"
        size={14}
        color={AppStyles.colorSet.textSecondary}
      >
        {t('escalation.description')}
      </CustomText>

      {error && (
        <View style={styles.inlineError}>
          <Feather name="alert-circle" size={14} color="#B91C1C" />
          <CustomText style={styles.inlineErrorText} size={13} color="#B91C1C">
            {error}
          </CustomText>
        </View>
      )}

      {/* Enable/Disable toggle */}
      <View style={styles.toggleRow}>
        <View style={{ flex: 1 }}>
          <CustomText font="medium" size={15} color={AppStyles.colorSet.textPrimary}>
            {t('escalation.enableSystem')}
          </CustomText>
          <CustomText size={12} color={AppStyles.colorSet.textMuted}>
            {t('escalation.enableSystemHint')}
          </CustomText>
        </View>
        <ToggleSwitch
          value={settings.enabled}
          onValueChange={(val) => handleChange('enabled', val)}
          disabled={readOnly}
        />
      </View>

      {settings.enabled && (
        <>
          {/* Urgent Only toggle */}
          <View style={styles.toggleRow}>
            <View style={{ flex: 1 }}>
              <CustomText font="medium" size={15} color={AppStyles.colorSet.textPrimary}>
                {t('escalation.urgentOnly')}
              </CustomText>
              <CustomText size={12} color={AppStyles.colorSet.textMuted}>
                {t('escalation.urgentOnlyHint')}
              </CustomText>
            </View>
            <ToggleSwitch
              value={settings.urgentOnly}
              onValueChange={(val) => handleChange('urgentOnly', val)}
              disabled={readOnly}
            />
          </View>

          {/* Unassigned Only toggle */}
          <View style={styles.toggleRow}>
            <View style={{ flex: 1 }}>
              <CustomText font="medium" size={15} color={AppStyles.colorSet.textPrimary}>
                {t('escalation.unassignedOnly')}
              </CustomText>
              <CustomText size={12} color={AppStyles.colorSet.textMuted}>
                {t('escalation.unassignedOnlyHint')}
              </CustomText>
            </View>
            <ToggleSwitch
              value={settings.unassignedOnly}
              onValueChange={(val) => handleChange('unassignedOnly', val)}
              disabled={readOnly}
            />
          </View>

          {/* Assigned Tickets Section */}
          {!settings.unassignedOnly && (
          <View style={styles.sectionCard}>
            <View style={styles.sectionHeader}>
              <Feather name="user" size={16} color={AppStyles.colorSet.brandPrimary} />
              <CustomText
                style={{ marginLeft: 8 }}
                font="semiBold"
                size={15}
                color={AppStyles.colorSet.textPrimary}
              >
                {t('escalation.assignedTickets')}
              </CustomText>
            </View>

            {!settings.urgentOnly && (
              <>
                <CustomText style={styles.subsectionLabel} font="medium" size={13} color={AppStyles.colorSet.textSecondary}>
                  {t('escalation.normalPriority')}
                </CustomText>
                {renderIntervalInput(t('escalation.reminder'), 'reminderIntervalHours', t('escalation.reminderHint'))}
                {renderIntervalInput(t('escalation.escalationLabel'), 'escalationIntervalHours', t('escalation.escalationHint'))}
              </>
            )}

            <CustomText style={[styles.subsectionLabel, !settings.urgentOnly && { marginTop: 12 }]} font="medium" size={13} color={AppStyles.colorSet.error}>
              {t('escalation.urgentPriority')}
            </CustomText>
            {renderIntervalInput(t('escalation.reminder'), 'urgentReminderIntervalHours')}
            {renderIntervalInput(t('escalation.escalationLabel'), 'urgentEscalationIntervalHours')}
          </View>
          )}

          {/* Unassigned Tickets Section */}
          <View style={styles.sectionCard}>
            <View style={styles.sectionHeader}>
              <Feather name="users" size={16} color="#EA580C" />
              <CustomText
                style={{ marginLeft: 8 }}
                font="semiBold"
                size={15}
                color={AppStyles.colorSet.textPrimary}
              >
                {t('escalation.unassignedTickets')}
              </CustomText>
            </View>

            {!settings.urgentOnly && (
              <>
                <CustomText style={styles.subsectionLabel} font="medium" size={13} color={AppStyles.colorSet.textSecondary}>
                  {t('escalation.normalPriority')}
                </CustomText>
                {renderIntervalInput(t('escalation.alertAdmins'), 'unassignedReminderIntervalHours', t('escalation.alertAdminsHint'))}
                {renderIntervalInput(t('escalation.escalationLabel'), 'unassignedEscalationIntervalHours')}
              </>
            )}

            <CustomText style={[styles.subsectionLabel, !settings.urgentOnly && { marginTop: 12 }]} font="medium" size={13} color={AppStyles.colorSet.error}>
              {t('escalation.urgentPriority')}
            </CustomText>
            {renderIntervalInput(t('escalation.alertAdmins'), 'unassignedUrgentReminderIntervalHours')}
            {renderIntervalInput(t('escalation.escalationLabel'), 'unassignedUrgentEscalationIntervalHours')}
          </View>

          {/* Cutoff Date */}
          <View style={styles.sectionCard}>
            <View style={styles.sectionHeader}>
              <Feather name="calendar" size={16} color={AppStyles.colorSet.textSecondary} />
              <CustomText
                style={{ marginLeft: 8 }}
                font="semiBold"
                size={15}
                color={AppStyles.colorSet.textPrimary}
              >
                {t('escalation.cutoffDate')}
              </CustomText>
            </View>
            <CustomText size={12} color={AppStyles.colorSet.textMuted} style={{ marginBottom: 10 }}>
              {t('escalation.cutoffDateHint')}
            </CustomText>
            {settings.enabledAt ? (
              <CutoffDateInput
                value={settings.enabledAt}
                onChange={(ts) => handleChange('enabledAt', ts)}
                readOnly={readOnly}
              />
            ) : (
              <CustomText size={13} color={AppStyles.colorSet.textMuted} style={{ fontStyle: 'italic' }}>
                {t('escalation.cutoffDateUnset')}
              </CustomText>
            )}
          </View>

          {/* Ticket Age-Off (opt-in) */}
          <View style={styles.sectionCard}>
            <View style={styles.sectionHeader}>
              <Feather name="clock" size={16} color={AppStyles.colorSet.textSecondary} />
              <CustomText
                style={{ marginLeft: 8 }}
                font="semiBold"
                size={15}
                color={AppStyles.colorSet.textPrimary}
              >
                {t('escalation.ticketAgeOff')}
              </CustomText>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
              <View style={{ flex: 1 }}>
                <CustomText font="medium" size={14} color={AppStyles.colorSet.textPrimary}>
                  {t('escalation.autoExpire')}
                </CustomText>
                <CustomText size={12} color={AppStyles.colorSet.textMuted}>
                  {t('escalation.autoExpireHint')}
                  Use with caution — aged-off tickets may go unnoticed.
                </CustomText>
              </View>
              <ToggleSwitch
                value={settings.ticketAgeOffEnabled}
                onValueChange={(val) => handleChange('ticketAgeOffEnabled', val)}
                disabled={readOnly}
              />
            </View>
            {settings.ticketAgeOffEnabled &&
              renderIntervalInput(t('escalation.maxAge'), 'ticketCutoffDays', t('escalation.maxAgeHint'), 'days')}
          </View>
        </>
      )}

      {/* Action buttons — hidden in read-only (mobile) mode */}
      {!readOnly && (
      <View style={styles.actionsRow}>
        {settings.enabled && (
          <Pressable
            style={({ pressed, hovered, focused }) => [
              styles.resetButton,
              { cursor: 'pointer' },
              (hovered || focused) && styles.resetButtonHovered,
              pressed && styles.buttonPressed,
            ]}
            onPress={handleResetDefaults}
          >
            <Feather name="rotate-ccw" size={14} color={AppStyles.colorSet.textSecondary} />
            <CustomText style={{ marginLeft: 6 }} font="medium" size={13} color={AppStyles.colorSet.textSecondary}>
              {t('escalation.resetDefaults')}
            </CustomText>
          </Pressable>
        )}

        <Pressable
          style={({ pressed, hovered, focused }) => [
            styles.saveButton,
            { cursor: 'pointer' },
            hasChanges && !saving && (hovered || focused) && styles.saveButtonHovered,
            (!hasChanges || saving) && styles.buttonDisabled,
            pressed && styles.buttonPressed,
          ]}
          onPress={handleSave}
          disabled={!hasChanges || saving}
        >
          {saving ? (
            <ActivityIndicator size="small" color="#000" />
          ) : (
            <>
              <Feather name="save" size={14} color="#000" />
              <CustomText style={{ marginLeft: 6 }} font="medium" size={14} color="#000">
                {t('escalation.saveChanges')}
              </CustomText>
            </>
          )}
        </Pressable>
      </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 4,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: hp(3),
  },
  loadingText: {
    marginLeft: 10,
  },
  heading: {
    marginTop: hp(1),
    marginBottom: hp(0.5),
  },
  explanationText: {
    marginBottom: hp(1.5),
    lineHeight: 20,
  },
  inlineError: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FEF2F2',
    padding: 10,
    borderRadius: 8,
    marginBottom: hp(1),
  },
  inlineErrorText: {
    marginLeft: 8,
    flex: 1,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    marginBottom: hp(1.5),
  },
  sectionCard: {
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    marginBottom: hp(1.5),
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  subsectionLabel: {
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  inputLabelContainer: {
    flex: 1,
    marginRight: 16,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E5E5',
  },
  stepButton: {
    width: 44,
    paddingVertical: 8,
    justifyContent: 'center',
    alignItems: 'center',
  },
  stepButtonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
    borderRadius: 8,
  },
  valueDisplay: {
    alignItems: 'center',
    width: 52,
    paddingVertical: 4,
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderColor: '#E5E5E5',
  },
  valueDisplayHovered: {
    backgroundColor: AppStyles.stateSet.hover,
  },
  valueInput: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 16,
    color: AppStyles.colorSet.textPrimary,
    textAlign: 'center',
    minWidth: 48,
    paddingVertical: 2,
  },
  actionsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: hp(0.5),
    marginBottom: hp(2),
  },
  resetButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E5E5',
  },
  resetButtonHovered: {
    borderColor: AppStyles.colorSet.brandPrimary,
    backgroundColor: AppStyles.stateSet.hover,
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.brandPrimary,
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  saveButtonHovered: {
    opacity: 0.92,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 2,
  },
  buttonPressed: {
    opacity: 0.8,
  },
  buttonDisabled: {
    opacity: 0.5,
  },
  cutoffDateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  cutoffDateInput: {
    fontFamily: 'Poppins_600SemiBold',
    fontSize: 15,
    color: AppStyles.colorSet.textPrimary,
    marginLeft: 8,
    borderBottomWidth: 1,
    borderBottomColor: AppStyles.colorSet.brandPrimary,
    minWidth: 110,
    paddingVertical: 2,
  },
});

export default EscalationSettingsSection;
